import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';

/**
 * PlatformService
 *
 * Detects the current platform (web, iOS, or Android) and provides
 * platform-specific configurations for OAuth redirect URIs.
 *
 * This service is critical for mobile authentication to work correctly
 * as iOS and Android use custom URL schemes (myabvv://oauth-callback)
 * while web uses standard HTTP(S) URLs.
 */
@Injectable({
  providedIn: 'root'
})
export class PlatformService {
  private _platform: 'web' | 'ios' | 'android';

  constructor() {
    this._platform = this.detectPlatform();
  }

  /**
   * Check if the current platform is web
   */
  isWeb(): boolean {
    return this._platform === 'web';
  }

  /**
   * Check if the current platform is iOS
   */
  isiOS(): boolean {
    return this._platform === 'ios';
  }

  /**
   * Check if the current platform is Android
   */
  isAndroid(): boolean {
    return this._platform === 'android';
  }

  /**
   * Check if the current platform is mobile (iOS or Android)
   */
  isMobile(): boolean {
    return this._platform === 'ios' || this._platform === 'android';
  }

  /**
   * Get the current platform identifier
   */
  getPlatform(): 'web' | 'ios' | 'android' {
    return this._platform;
  }

  /**
   * Get the platform-specific OAuth redirect URI
   *
   * - Mobile (iOS/Android): myabvv://oauth-callback
   * - Web: {origin}/auth/callback
   *
   * Both mobile platforms use the custom scheme, which is verified working end to end
   * including the itsme same-device flow. On iOS the login runs in Safari (see
   * AuthService.openAuthorizationUrl) and Safari asks "Open in MijnABVV?" before handing
   * the redirect to the app — one extra tap, and the login completes.
   *
   * A Universal Link (https://my.abvv.be/oauth-callback) would remove that prompt and was
   * tried on 2026-10-01 across three builds and two phones. It never opened the app:
   * swcd reported the association approved but the URL not matching, and Apple's CDN —
   * which is where iOS fetches the apple-app-site-association, not from our server — was
   * still serving a copy predating the fix. An isolated openURL test with
   * applinks:my.abvv.be?mode=developer produced no swcd verdict at all.
   *
   * Two candidate causes were never separated: Apple's CDN cache, or universal links not
   * firing on a 302 redirect (they are designed for user taps on links, and our callback
   * always arrives as a redirect from sso.abvv.be). If this is revisited, check
   * https://app-site-association.cdn-apple.com/a/v1/my.abvv.be FIRST — the server copy
   * being correct tells you nothing.
   *
   * Everything needed for a retry is already in place and harmless meanwhile:
   * applinks:my.abvv.be in App.entitlements, /oauth-callback in the AASA (components
   * format, openshift_myabvv!38), the /oauth-callback Angular route (!811), and the
   * Keycloak redirect URI. Retrying costs this one line plus a reinstall.
   *
   * @returns The redirect URI for the current platform
   */
  getRedirectUri(): string {
    switch (this._platform) {
      case 'ios':
      case 'android':
        return 'myabvv://oauth-callback';
      case 'web':
      default:
        return window.location.origin + '/auth/callback';
    }
  }

  /**
   * Get the platform-specific post-logout redirect URI
   *
   * - Mobile (iOS/Android): myabvv://landing
   * - Web: {origin}/auth/landing
   *
   * @returns The redirect URI after logout
   */
  getPostLogoutRedirectUri(): string {
    switch (this._platform) {
      case 'ios':
      case 'android':
        return 'myabvv://landing';
      case 'web':
      default:
        return window.location.origin + '/auth/landing';
    }
  }

  /**
   * Detect the current platform using Capacitor.getPlatform()
   *
   * @returns 'web', 'ios', or 'android'
   */
  private detectPlatform(): 'web' | 'ios' | 'android' {
    const platform = Capacitor.getPlatform();

    if (platform === 'ios') {
      return 'ios';
    } else if (platform === 'android') {
      return 'android';
    } else {
      return 'web';
    }
  }
}
